import { useState, useRef, useEffect } from 'react'
import { useCrosswordStore } from '../../store'
import { PencilIcon, DuplicateIcon, TrashIcon, PlusIcon } from '../Icons'

/**
 * Sidebar tab body listing drafts. The active draft is highlighted; clicking
 * a row switches to that draft. Was previously a toolbar dropdown; now lives
 * as a flat tab in ExploreColumn.
 */
export function DraftSelector() {
  const {
    drafts,
    activeDraftId,
    createDraft,
    switchDraft,
    renameDraft,
    deleteDraft,
    duplicateDraft,
  } = useCrosswordStore()

  const [editingDraftId, setEditingDraftId] = useState<string | null>(null)
  const [editingName, setEditingName] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (editingDraftId && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [editingDraftId])

  const handleStartEdit = (draftId: string, name: string, e: React.MouseEvent) => {
    e.stopPropagation()
    setEditingDraftId(draftId)
    setEditingName(name)
  }

  const handleFinishEdit = () => {
    if (editingDraftId && editingName.trim()) {
      renameDraft(editingDraftId, editingName.trim())
    }
    setEditingDraftId(null)
    setEditingName('')
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleFinishEdit()
    else if (e.key === 'Escape') {
      setEditingDraftId(null)
      setEditingName('')
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    return date.toLocaleDateString()
  }

  const handleSwitchDraft = (draftId: string) => {
    if (draftId !== activeDraftId) switchDraft(draftId)
  }

  const handleDuplicate = (draftId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    duplicateDraft(draftId)
  }

  const handleDelete = (draftId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    if (drafts.length === 1) return
    deleteDraft(draftId)
  }

  return (
    <div className="flex flex-col h-full -mx-3 -mb-3">
      <div className="px-3 py-2 bg-gray-50/80 border-b border-gray-100 shrink-0 flex items-center justify-between">
        <span className="text-xs font-semibold text-gray-700">Drafts</span>
        <button
          onClick={() => createDraft()}
          className="flex items-center gap-1 px-2 py-0.5 text-[11px] text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded"
          title="Create a new draft"
        >
          <PlusIcon className="w-3 h-3" />
          <span>New</span>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        {drafts.length === 0 ? (
          <div className="px-3 py-6 text-xs text-gray-400">
            No drafts yet. Click "New" to create one.
          </div>
        ) : (
          <div className="py-1">
            {drafts.map(draft => (
              <div
                key={draft.id}
                onClick={() => handleSwitchDraft(draft.id)}
                className={`group flex items-center gap-2 px-3 py-1.5 cursor-pointer ${
                  draft.id === activeDraftId ? 'bg-blue-50' : 'hover:bg-gray-50'
                }`}
              >
                {editingDraftId === draft.id ? (
                  <input
                    ref={inputRef}
                    type="text"
                    value={editingName}
                    onChange={e => setEditingName(e.target.value)}
                    onBlur={handleFinishEdit}
                    onKeyDown={handleKeyDown}
                    onClick={e => e.stopPropagation()}
                    className="flex-1 px-1 py-0.5 text-xs border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                ) : (
                  <>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-gray-900 truncate">
                        {draft.name}
                      </div>
                      <div className="text-[10px] text-gray-400">
                        {formatDate(draft.updatedAt)} · {draft.grid.placements.length} placed
                      </div>
                    </div>
                    <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={(e) => handleStartEdit(draft.id, draft.name, e)}
                        className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                        title="Rename"
                      >
                        <PencilIcon className="w-3 h-3" />
                      </button>
                      <button
                        onClick={(e) => handleDuplicate(draft.id, e)}
                        className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                        title="Duplicate"
                      >
                        <DuplicateIcon className="w-3 h-3" />
                      </button>
                      {drafts.length > 1 && (
                        <button
                          onClick={(e) => handleDelete(draft.id, e)}
                          className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
                          title="Delete"
                        >
                          <TrashIcon className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
